export interface WeaponIntrinsicAttr {
  stat: string;
  value: number;
  max?: number;
}

export interface WeaponBuiltInMod {
  slot: string;
  stat?: string | null;
  value?: number;
  name_ru?: string;
  name_en?: string;
}

export interface WeaponAttrPoolEntry {
  stat: string;
  max: number;
}

export interface Weapon {
  id: string;
  kind: 'base' | 'named' | 'exotic';
  category: string;
  baseDamage: number;
  rpm: number;
  magazine: number;
  reloadSeconds: number;
  optimalRange?: number;
  headshotMultiplier: number;
  talentId?: string;
  brandId?: string;
  modSlots: string[];
  /** Exotic talent type: amp|kill|stacks|shot_cover|hs_kill|swap_in|conditional|no_reload */
  talType?: string;
  /** % bonus per stack (or total for amp/swap_in) */
  talBonus?: number;
  /** Max stacks (for stacking talents) */
  talMax?: number;
  isBurst?: boolean;
  burstNote?: string;
  /** [0]+[1] = fixed intrinsics; [2] = default for the user-selectable 3rd attribute slot. */
  intrinsicAttrs?: WeaponIntrinsicAttr[];
  /** Locked exotic mods (can't swap in UI). */
  builtInMods?: WeaponBuiltInMod[];
  /** Per-weapon override of the 3rd-slot attribute pool. If absent — universal pool is used. */
  attrSlot3Pool?: WeaponAttrPoolEntry[];
}

export interface Brand {
  id: string;
  bonuses: Array<{ pieces: number; bonus: { stat: string; value: number } }>;
  core: string;
  dlc?: string | null;
}

export interface GearSet {
  id: string;
  type: 'red' | 'blue' | 'yellow' | 'purple';
  numericBonuses: Array<{ pieces: number; bonus: { stat: string; value: number } }>;
  chestTalentId?: string;
  backpackTalentId?: string;
  dlc?: string | null;
}

export interface Talent {
  id: string;
  kind: 'weapon' | 'chest' | 'backpack' | 'gloves';
  bonusType: 'additive' | 'amplified' | 'conditional';
  bonuses: Array<{ stat: string; value: number }>;
  applicableWeaponClasses?: string[];
  hasPerfectVariant?: boolean;
}

export interface NamedGear {
  id: string;
  slot: 'chest' | 'backpack' | 'gloves' | 'mask' | 'holster' | 'kneepads';
  brand?: string;
  /** Effective core stat: 'wd' (red), 'armor' (blue), 'skill_tier' (yellow), 'variable' (Investor/NinjaBike). */
  core: 'wd' | 'armor' | 'skill_tier' | 'variable' | string;
  /** Raw color tag from game data (red/blue/yellow/variable). */
  coreAttribute?: 'red' | 'blue' | 'yellow' | 'variable';
  /** Layout: 'standard' (3 core + 2 minor), '3core' (3 cores only — Memento/Harrier/NinjaBike BP), 'variable' (Investor — user-pick). */
  layoutType?: 'standard' | '3core' | 'variable';
  fixedAttrs: Array<{ stat: string; value: number }>;
  isExotic?: boolean;
  /** Bonuses granted when the exotic's unique talent is active (user-toggled). */
  activeBonuses?: Array<{ stat: string; value: number; amp?: boolean }>;
  exoticMechanic?: string;
}

export interface GameData {
  weapons: Weapon[];
  brands: Brand[];
  sets: GearSet[];
  talents: Talent[];
  namedGear: NamedGear[];
  byId: {
    weapon: Map<string, Weapon>;
    brand: Map<string, Brand>;
    set: Map<string, GearSet>;
    talent: Map<string, Talent>;
    namedGear: Map<string, NamedGear>;
  };
}

// Build token — different per build to invalidate SW cache.
// Vite replaces `__BUILD_TS__` during build via define plugin; fall back to current timestamp in dev.
declare const __BUILD_TS__: string | undefined;
const BUILD_TOKEN = typeof __BUILD_TS__ !== 'undefined' ? __BUILD_TS__ : String(Date.now());

async function fetchJson<T>(url: string): Promise<T> {
  const sep = url.includes('?') ? '&' : '?';
  const r = await fetch(`${url}${sep}v=${BUILD_TOKEN}`, { cache: 'no-cache' });
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return (await r.json()) as T;
}

import { loadWeaponMods } from './data/weapon-mods.js';

export async function loadGameData(): Promise<GameData> {
  const [weaponsFile, brandsFile, setsFile, talentsFile, namedFile] = await Promise.all([
    fetchJson<{ weapons: Weapon[] }>('/data/weapons.json'),
    fetchJson<{ brands: Brand[] }>('/data/brands.json'),
    fetchJson<{ sets: GearSet[] }>('/data/gear-sets.json'),
    fetchJson<{ talents: Talent[] }>('/data/talents.json'),
    fetchJson<{ items: NamedGear[] }>('/data/named-gear.json'),
    loadWeaponMods(),
  ]);

  const data: GameData = {
    weapons: weaponsFile.weapons,
    brands: brandsFile.brands,
    sets: setsFile.sets,
    talents: talentsFile.talents,
    namedGear: namedFile.items,
    byId: {
      weapon: new Map(weaponsFile.weapons.map((w) => [w.id, w])),
      brand: new Map(brandsFile.brands.map((b) => [b.id, b])),
      set: new Map(setsFile.sets.map((s) => [s.id, s])),
      talent: new Map(talentsFile.talents.map((t) => [t.id, t])),
      namedGear: new Map(namedFile.items.map((n) => [n.id, n])),
    },
  };
  return data;
}
